import { GRID_SIZE, OsGrid } from "@outschool/backpack";
import { SearchListingsQuery } from "@outschool/gql-frontend-generated";
import { Screen } from "@outschool/ui-utils";
import React, { useMemo } from "react";

import { MAX_NUMBER_OF_CLASSES_TO_COMPARE } from "../../classComparison/consts";
import SearchResultsGalleryExperimentListing, {
  BlankSearchResultsGalleryExperimentListing,
} from "../../listings/SearchResultsGalleryExperimentListing";
import { ListingsProp } from "../common";

type SearchResultGalleryExperimentProps = ListingsProp & {
  hasPublicFundingFilter: boolean;
};

// Constants for the grid layout.
const COLUMN_WIDTH_PX = 320;
const COLUMN_GAP_PX = 20;

// How often to debounce the resize event when calculating the number of columns.
const RESIZE_DEBOUNCE_MS = 200;

// Maximum number of columns to render.
const MAX_NUMBER_OF_COLUMNS = 4;

/**
 * Experimental gallery/grid layout for search listings.
 *
 * Experiment: SearchResultsGalleryDesign
 *
 * https://www.figma.com/design/H5txmPILhqRX1MNQqCBmSh/Search-And-Recs-Pod?node-id=922-9792&p=f&t=eSWxsbJcznftrWuE-0'
 *
 * TODO: When we launch this experiment, we should update the name of this component to something more descriptive,
 * like ActivityCardsGrid or similar.
 *
 * Note that the current implementation makes no changes for mobile cards (this component is only rendered on desktop).
 * See ListingsHelpers.tsx (mobile version) for more details.
 */
export default function SearchResultsGalleryExperiment({
  listings,
  isComparing,
  tracking,
  hasPublicFundingFilter,
  selectedListings,
  setSelectedListings,
}: SearchResultGalleryExperimentProps) {
  const onSelectToCompare = (
    listing: SearchListingsQuery["searchListings"]["listings"][0],
  ) => {
    if (selectedListings.includes(listing.activity.uid)) {
      setSelectedListings(
        selectedListings.filter(uid => uid !== listing.activity.uid),
      );
    } else if (selectedListings.length < MAX_NUMBER_OF_CLASSES_TO_COMPARE) {
      setSelectedListings([...selectedListings, listing.activity.uid]);
    }
  };

  // Screen width in pixels, for calculating how many columns can fit in the screen width.
  const [screenWidth] = Screen.useWindowSize({
    debounceMs: RESIZE_DEBOUNCE_MS,
  });
  const numColumns = useMemo(() => {
    return Math.min(
      MAX_NUMBER_OF_COLUMNS,
      // Technically, a slight underestimate of how many columns can fit in the screen width (really there should only be N - 1 gaps).
      // But an underestimate is fine since numColumns controls the OsGrid number of columns.
      Math.floor(screenWidth / (COLUMN_WIDTH_PX + COLUMN_GAP_PX)),
    );
  }, [screenWidth]);

  // We need to display blank cards to fill out the last row if the number of listings is not a multiple of the number of columns.
  // This is a fundamental limitation of the flex-based OsGrid component. We can't have justifyContent: "center" and also have
  // start-justified rows when the rows are not full.
  const numBlankCardsToDisplay = useMemo(() => {
    return listings.length % numColumns === 0
      ? 0
      : numColumns - (listings.length % numColumns);
  }, [numColumns, listings.length]);

  return (
    <OsGrid
      columnSpacing={Math.floor(COLUMN_GAP_PX / GRID_SIZE)}
      rowSpacing={3}
      columns={numColumns}
      container
      sx={{
        justifyContent: "center",
      }}
    >
      {listings.map((listing, index) => {
        const isSelected = selectedListings.includes(listing.activity.uid);
        const isCheckboxDisabled =
          selectedListings.length >= MAX_NUMBER_OF_CLASSES_TO_COMPARE &&
          !isSelected;

        // Calculate tracking properties
        // All indices are 1-indexed.
        const columnIndex = (index % numColumns) + 1;
        const rowIndex = Math.floor(index / numColumns) + 1;

        // Don't want startIndex in the tracking properties.
        const { startIndex, ...restTracking } = tracking ?? {};
        const trackingProps = {
          ...restTracking,
          order: (startIndex ?? 0) + index + 1,
          // Tracking properties for the gallery layout.
          numColumns: numColumns,
          columnIndex,
          rowIndex,
        };

        return (
          <SearchResultsGalleryExperimentListing
            key={listing.activity.uid}
            listing={listing}
            listingIndex={index}
            trackingProperties={trackingProps}
            disabled={isComparing}
            openInNewTab
            showFundingBadge={hasPublicFundingFilter}
            isSelected={isSelected}
            isComparing={isComparing}
            isCheckboxDisabled={isCheckboxDisabled}
            onSelectToCompare={() => onSelectToCompare(listing)}
          />
        );
      })}
      {Array.from({ length: numBlankCardsToDisplay }).map((_, i) => (
        <BlankSearchResultsGalleryExperimentListing key={`blank-card-${i}`} />
      ))}
    </OsGrid>
  );
}
